import i18n from './utils/translations';
import { I18nextProvider } from 'react-i18next';
import { IntelliProveAPIProvider } from './providers/api';
import { Outlet, useNavigate } from 'react-router-dom';
import { getAuthFromSearchParams } from './utils/path/search_params';
import { useEffect } from 'react';
import { errorEvent, LogLevel } from '~errors/emitter';
import { isBenignBrowserError } from '~errors/benign_errors';
import { ConsoleErrorLogger } from '~errors/error_logger';
import { ErrorNavigator } from '~errors/error_navigator';
import IntelliProveAPIError from '~services/errors/intelliprove_api_exception';
import { SettingsProvider } from '~providers/settings';
import { PostHogProvider } from 'posthog-js/react';
import { getAuthContextFromJwt } from '~utils/jwt';
import useProductAnalytics from '~hooks/product_analytics';
import useProductSurveys from '~hooks/product_surveys';
import { usePostMessageResize } from '~hooks/resize_notifier';
import _refiner from 'refiner-js';

const APIURL = import.meta.env.VITE_API_URL;

export interface iAppBootstrap {}

function AppBootstrap({}: iAppBootstrap) {
  const [authKey, authMethod] = getAuthFromSearchParams();
  const { analyticsEnabled, initialiseAnalytics, getUserReferenceId, postHogApiKey, postHogOptions } = useProductAnalytics();
  const { productSurveysEnabled, initialiseProductSurveys } = useProductSurveys();
  const navigate = useNavigate();

  usePostMessageResize();

  useEffect(() => {
    const consoleLogger = new ConsoleErrorLogger();
    const errorNavigator = new ErrorNavigator(navigate);

    const unsub = errorEvent.subscribe((evt) => {
      if (evt.silent === true) return;

      consoleLogger.handle(evt);

      // `silent` drops the event entirely; this only skips the error page.
      if (evt.disableErrorNavigator === true) return;

      errorNavigator.handle(evt);
    });

    // Global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      const text = message.toString();
      const benign = isBenignBrowserError(text);

      errorEvent.emit({
        error: error,
        message: text,
        context: {
          source: source,
          lineno: lineno,
          colno: colno,
        },
        // Browser noise: log it, but never show the error page for it.
        loglevel: benign ? LogLevel.Warning : LogLevel.Error,
        disableErrorNavigator: benign,
      });

      return benign;
    };

    window.onunhandledrejection = (event) => {
      const error = event.reason;
      const message = error instanceof Error ? error.message : 'Unhandled rejection';

      // An unhandled rejection is by definition one nobody awaited - a
      // fire-and-forget side effect such as an embedded widget drawing itself.
      // Those must not replace the whole dashboard with an error page. Real
      // backend failures arrive as IntelliProveAPIError and still do.
      const fatal = error instanceof IntelliProveAPIError;

      errorEvent.emit({
        error: error,
        message: message.toString(),
        context: {
          source: "Unhandled 'catch' from Promise",
        },
        loglevel: fatal ? LogLevel.Error : LogLevel.Warning,
        disableErrorNavigator: !fatal,
      });
    };

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!analyticsEnabled) return;

    const authContext = getAuthContextFromJwt(authKey);

    if (!authContext.userId) {
      console.warn('No User ID found in auth token.');
      return;
    }

    initialiseAnalytics(authContext);

    if (productSurveysEnabled) {
      const userReferenceId = getUserReferenceId();
      initialiseProductSurveys(userReferenceId, authContext.customer);
    }
  }, []);

  return (
    <>
      <IntelliProveAPIProvider apiurl={APIURL} authentication={authKey} authenticationMethod={authMethod}>
        <I18nextProvider i18n={i18n}>
          <SettingsProvider>
            {analyticsEnabled ? (
              <PostHogProvider apiKey={postHogApiKey} options={postHogOptions}>
                <Outlet />
              </PostHogProvider>
            ) : (
              <Outlet />
            )}
          </SettingsProvider>
        </I18nextProvider>
      </IntelliProveAPIProvider>
    </>
  );
}

export default AppBootstrap;
